/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { Dispatch, SetStateAction, useEffect, useState } from 'react';

export interface InputState<T> {
    value: T;
    setValue: Dispatch<SetStateAction<T>>;
    errorMessage: string;
    setErrorMessage: (value: string) => void;
    isValid: boolean;
    maxLength?: number;
}

export interface InputStateProps<T> {
    initialValue: T;
    setValue?: Dispatch<SetStateAction<T>>;
    validation?: (value: T) => boolean;
    maxLength?: number;
}

export function useInputState<T>(props: InputStateProps<T>): InputState<T> {
    const { initialValue, validation, maxLength } = props;

    const [value, setValue] = useState(initialValue);
    const [errorMessage, setErrorMessage] = useState('');
    const [isValid, setIsValid] = useState(false);

    useEffect((): void => {
        setIsValid(!errorMessage && (!validation || validation(value)));
    }, [value, validation, errorMessage]);

    const setAllValues: Dispatch<SetStateAction<T>> = (action: T | ((prevState: T) => T)): void => {
        setValue(action);
        if (!!props.setValue) {
            props.setValue(action);
        }
    };

    return {
        value,
        setValue: setAllValues,
        errorMessage,
        setErrorMessage,
        isValid,
        maxLength,
    };
}
